import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {FileUpload, FileUploadCacheEntry} from '../../shared/models/file-upload';
import {HttpClient} from '@angular/common/http';
import {FILE_UPLOAD_BASE_URL} from './env.service';
import {ObjectId, ObjectType} from '../../shared/models/object.model';

@Injectable({
  providedIn: 'root',
})
export class FileUploadService {
  private readonly cacheTtlMs = 45_000;
  private fetches: { [uuid: string]: FileUploadCacheEntry } = {};


  constructor(
    private http: HttpClient,
  ) {
  }


  fetch(uuid: string): BehaviorSubject<FileUpload> {
    const cached = this.fetches[uuid];

    if (cached) {
      const age = Date.now() - cached.cachedAt;
      if (age < this.cacheTtlMs) {
        return cached.subject;
      }
      // Cache expired — re-fetch and emit on the existing subject so current subscribers get the new URL
      this.refreshCacheEntry(uuid, cached.subject);
      return cached.subject;
    }

    const bs$ = new BehaviorSubject<FileUpload>(null);
    this.addToCache(uuid, bs$);

    this.http.get<FileUpload>(`${FILE_UPLOAD_BASE_URL}/uploads/${uuid}`).subscribe(
      (response) => {
        bs$.next(response);
      },
    );

    return bs$;
  }


  create(file: File, objectId: ObjectId, objectType: ObjectType): Observable<FileUpload> {
    return new Observable<FileUpload>(
      (subscriber) => {
        const formData = new FormData();
        formData.append('file', file, file.name);
        formData.append('ObjectID', '' + objectId);
        formData.append('ObjectType', objectType);

        this.http.post<FileUpload>(`${FILE_UPLOAD_BASE_URL}/uploads`, formData).subscribe(
          (upload) => {
            this.addToCache(upload.UUID, new BehaviorSubject<FileUpload>(upload));

            subscriber.next(upload);
            subscriber.complete();
          },
          (error) => {
            subscriber.error(error);
          },
        );
      },
    );
  }


  delete(uuid: string): Observable<Object> {
    return new Observable<Object>(
      (subscriber) => {
        this.http.delete(`${FILE_UPLOAD_BASE_URL}/uploads/${uuid}`).subscribe(
          (result) => {
            subscriber.next(result);
            subscriber.complete();
          },
          (error) => {
            subscriber.error(error);
          },
        );
      },
    );
  }


  private refreshCacheEntry(uuid: string, subject: BehaviorSubject<FileUpload>): void {
    // Update cachedAt immediately to prevent concurrent re-fetch calls
    this.fetches[uuid].cachedAt = Date.now();
    this.http.get<FileUpload>(`${FILE_UPLOAD_BASE_URL}/uploads/${uuid}`).subscribe(
      (response) => {
        subject.next(response);
      },
    );
  }


  private addToCache(uuid: string, upload$: BehaviorSubject<FileUpload>): void {
    this.fetches[uuid] = {subject: upload$, cachedAt: Date.now()};
  }
}
